import moment from 'moment';
import HelperMixin from '../../Shared/HelperMixin.vue';

export default {
    name: 'NeedEstimates',
    mixins: [HelperMixin],

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails || !this.canViewPage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'واجهة بيانات الاحتياج متاحة لمستخدمي المرافق وإدارة الصيدلة.');
            this.href('/Home');
            return;
        }

        if (this.isMinistry) {
            this.loadBranches();
            // الطابور يُحمَّل فوراً لا عند فتح تبويبه: العدد على الزر هو ما
            // يُعلم الإدارة أن هناك ما ينتظرها، ولو انتظر الفتح لما عُلم به.
            this.loadPending();
        } else {
            // مستخدم مرفق: ورقة مرفقه مباشرة
            this.loadSheet();
        }
    },

    computed: {
        /** أي موظف تابع للمرفق (قرار العميل) أو مستخدم وزاري مخوَّل. */
        canViewPage() {
            return !!(this.loginDetails && (this.isFacilityCenterRole() || this.hasInventoryAndStockAccessUi()));
        },
        isMinistry() {
            return this.hasInventoryAndStockAccessUi();
        },
        /** عدد الأصناف التي أُدخل لها احتياج. */
        filledCount() {
            return this.rows.filter((r) => r.monthlyNeed != null && Number(r.monthlyNeed) > 0).length;
        },
        /** أسطر تغيّرت عن قيمتها المحفوظة — هي فقط ما يُرسل عند الحفظ. */
        dirtyRows() {
            return this.rows.filter((r) => {
                const current = r.monthlyNeed == null || r.monthlyNeed === '' ? 0 : Number(r.monthlyNeed);
                const original = r.originalMonthly == null ? 0 : Number(r.originalMonthly);
                return current !== original;
            });
        },
        hasConsumptionData() {
            return this.rows.some((r) => r.avgMonthlyConsumption != null);
        },

        /** نص زر الحفظ يختلف بالدور: المرفق يرسل للاعتماد، والإدارة تعتمد. */
        saveLabel() {
            return this.proposesOnly ? 'إرسال للاعتماد' : 'حفظ واعتماد';
        },
        /** اقتراحات المرفق التي لم يُبتّ فيها بعد — تُعرض له كتنبيه. */
        myPendingCount() {
            return this.rows.filter((r) => r.proposedNeed != null).length;
        },
        /** اقتراحات رُدّت ولم يُرسل بعدها اقتراح جديد. */
        myRejectedCount() {
            return this.rows.filter((r) => r.proposedNeed == null && r.rejectReason).length;
        },
    },

    filters: {
        moment(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY');
        },
    },

    data() {
        return {
            mode: 'sheet', // sheet = حسب المرفق | product = حسب الصنف (وزارة فقط)

            branches: [],
            selectedBranchId: null,

            search: '',
            rows: [],
            consumptionMonths: 6,
            loadingSheet: false,

            // ── دورة الاعتماد ──
            // المرفق يقترح ولا يعتمد؛ إدارة الصيدلة تعتمد مباشرة وتبتّ في المعلّق.
            proposesOnly: false,
            pendingRows: [],
            pendingSelected: [],
            pendingSearch: '',
            pendingBranchId: null,
            loadingPending: false,

            // مقارنة حسب الصنف
            productOptions: [],
            productSearchLoading: false,
            selectedFormularyItemId: null,
            productInfo: null,
            productRows: [],
        };
    },

    methods: {
        // ─── الأسبوعي والسنوي: حساب لحظي أمام المستخدم ───────────────────
        weeklyOf(monthly) {
            const m = Number(monthly);
            if (!Number.isFinite(m) || m <= 0) return null;
            return Math.ceil(m / 4); // التقريب لأعلى — قرار العميل
        },
        annualOf(monthly) {
            const m = Number(monthly);
            if (!Number.isFinite(m) || m <= 0) return null;
            return m * 12;
        },

        apiError(err, fallback) {
            const msg = err && err.response && err.response.data ? err.response.data : fallback;
            this.$helper.ShowMessage('error', 'خطأ', msg);
        },

        // ─── ورقة المرفق ─────────────────────────────────────────────────
        loadBranches() {
            this.$http.GetAllBranches()
                .then((res) => {
                    this.branches = (res && res.data && res.data.info) || [];
                })
                .catch(() => { this.branches = []; });
        },

        onBranchChange() {
            this.rows = [];
            if (this.selectedBranchId) this.loadSheet();
        },

        loadSheet() {
            if (this.isMinistry && !this.selectedBranchId) return;
            this.loadingSheet = true;
            this.$blockUI.Start();
            this.$http.GetNeedEstimatesSheet(this.isMinistry ? this.selectedBranchId : null, this.search || null)
                .then((res) => {
                    this.$blockUI.Stop();
                    this.loadingSheet = false;
                    const d = (res && res.data) || {};
                    this.consumptionMonths = d.consumptionMonths || 6;
                    this.proposesOnly = !!d.proposesOnly;
                    this.rows = (d.info || []).map((r) => {
                        // الخانة تعرض الاقتراح المعلّق إن وُجد، وإلا المعتمَد.
                        // ولولا ذلك لرأى المرفق رقمه «يرجع» بعد الإرسال فيظن أنه ضاع.
                        const shown = r.proposedNeed != null ? r.proposedNeed : r.monthlyNeed;
                        return {
                            nationalFormularyItemsId: r.nationalFormularyItemsId,
                            name: r.name,
                            nationalCode: r.nationalCode,
                            baseUnitName: r.baseUnitName,
                            monthlyNeed: shown,
                            originalMonthly: shown,
                            approvedNeed: r.monthlyNeed,
                            proposedNeed: r.proposedNeed,
                            proposedNote: r.proposedNote,
                            proposedOn: r.proposedOn,
                            approvedOn: r.approvedOn,
                            rejectReason: r.rejectReason,
                            rejectedOn: r.rejectedOn,
                            avgMonthlyConsumption: r.avgMonthlyConsumption,
                            countShortage: r.countShortage,
                            damagedLastYear: r.damagedLastYear,
                            modifiedOn: r.modifiedOn,
                        };
                    });
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.loadingSheet = false;
                    this.rows = [];
                    this.apiError(err, 'تعذّر تحميل بيانات الاحتياج.');
                });
        },

        /** اعتماد الاستهلاك الفعلي احتياجاً شهرياً لصنف واحد. */
        applyActual(row) {
            if (row.avgMonthlyConsumption == null) return;
            row.monthlyNeed = Number(row.avgMonthlyConsumption);
        },

        /** اعتماد الفعلي لكل الأصناف التي لها استهلاك مسجَّل. */
        applyActualAll() {
            const candidates = this.rows.filter((r) => r.avgMonthlyConsumption != null);
            if (!candidates.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'لا توجد بيانات استهلاك فعلي بعد — تظهر عند بدء المرافق بتسجيل صرفها الداخلي.');
                return;
            }
            this.$confirm(
                `سيُعبَّأ الاحتياج الشهري من متوسط الاستهلاك الفعلي لـ ${candidates.length} صنفاً (يبقى الحفظ بيدك). متابعة؟`,
                'تحديث من الاستهلاك الفعلي',
                { confirmButtonText: 'تعبئة', cancelButtonText: 'تراجع', type: 'warning' }
            ).then(() => {
                candidates.forEach((r) => { r.monthlyNeed = Number(r.avgMonthlyConsumption); });
            }).catch(() => { });
        },

        saveAll() {
            const dirty = this.dirtyRows;
            if (!dirty.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'لا توجد تغييرات للحفظ.');
                return;
            }

            const bad = dirty.find((r) => r.monthlyNeed != null && r.monthlyNeed !== '' && Number(r.monthlyNeed) < 0);
            if (bad) {
                this.$helper.ShowMessage('warning', 'تنبيه', `قيمة غير صحيحة للصنف «${bad.name}».`);
                return;
            }

            // المرفق يُرسل اقتراحاً — والمبرر يقرأه من يعتمد، فيُطلب مرة واحدة.
            if (this.proposesOnly) {
                this.$prompt(
                    `سيُرسل ${dirty.length} صنفاً لاعتماد إدارة الصيدلة. مبرر مختصر (اختياري):`,
                    'إرسال الاحتياج للاعتماد',
                    { confirmButtonText: 'إرسال', cancelButtonText: 'تراجع', inputValue: '' }
                ).then(({ value }) => {
                    this.submitSheet(dirty, value ? String(value).trim() : null);
                }).catch(() => { });
                return;
            }

            this.submitSheet(dirty, null);
        },

        submitSheet(dirty, note) {
            const payload = {
                BranchesId: this.isMinistry ? Number(this.selectedBranchId) : null,
                Lines: dirty.map((r) => ({
                    NationalFormularyItemsId: r.nationalFormularyItemsId,
                    MonthlyNeed: r.monthlyNeed == null || r.monthlyNeed === '' ? 0 : Number(r.monthlyNeed),
                    Note: note,
                })),
            };

            this.$blockUI.Start();
            this.$http.SaveNeedEstimates(payload)
                .then((res) => {
                    this.$blockUI.Stop();
                    this.$helper.ShowMessage('success', 'تم', (res.data && res.data.message) || 'تم حفظ بيانات الاحتياج.');
                    this.loadSheet();
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.apiError(err, 'تعذّر حفظ بيانات الاحتياج.');
                });
        },

        // ─── طابور الاعتماد (إدارة الصيدلة) ──────────────────────────────

        loadPending() {
            this.loadingPending = true;
            this.$http.GetPendingNeedProposals(this.pendingBranchId || null, this.pendingSearch || null)
                .then((res) => {
                    this.loadingPending = false;
                    const d = (res && res.data) || {};
                    this.pendingRows = (d.info || []).map((r) => ({
                        branchesId: r.branchesId,
                        branchName: r.branchName,
                        nationalFormularyItemsId: r.nationalFormularyItemsId,
                        itemName: r.itemName,
                        nationalCode: r.nationalCode,
                        approvedNeed: r.approvedNeed,
                        proposedNeed: r.proposedNeed,
                        // القيمة القابلة للتعديل قبل الاعتماد — تبدأ من المقترَح.
                        decidedNeed: r.proposedNeed,
                        proposedNote: r.proposedNote,
                        proposedOn: r.proposedOn,
                        difference: r.difference,
                        damagedLastYear: r.damagedLastYear,
                    }));
                    this.pendingSelected = [];
                })
                .catch((err) => {
                    this.loadingPending = false;
                    this.pendingRows = [];
                    this.apiError(err, 'تعذّر تحميل الاقتراحات المعلّقة.');
                });
        },

        onPendingSelection(rows) {
            this.pendingSelected = rows || [];
        },

        approveSelected() {
            if (!this.pendingSelected.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'حدّد الاقتراحات المراد اعتمادها.');
                return;
            }

            const changed = this.pendingSelected.filter(
                (r) => Number(r.decidedNeed) !== Number(r.proposedNeed)
            ).length;

            const note = changed
                ? ` منها ${changed} باعتماد قيمة مختلفة عن المقترَح.`
                : '';

            this.$confirm(
                `اعتماد ${this.pendingSelected.length} اقتراحاً؟${note} يصير المعتمَد هو ما تعمل به الطلبات.`,
                'اعتماد الاحتياج',
                { confirmButtonText: 'اعتماد', cancelButtonText: 'تراجع', type: 'warning' }
            ).then(() => {
                const payload = {
                    Lines: this.pendingSelected.map((r) => ({
                        BranchesId: r.branchesId,
                        NationalFormularyItemsId: r.nationalFormularyItemsId,
                        ApprovedNeed: r.decidedNeed == null || r.decidedNeed === ''
                            ? Number(r.proposedNeed) : Number(r.decidedNeed),
                    })),
                };
                this.$blockUI.Start();
                this.$http.ApproveNeedProposals(payload)
                    .then((res) => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'تم', (res.data && res.data.message) || 'تم الاعتماد.');
                        this.loadPending();
                    })
                    .catch((err) => {
                        this.$blockUI.Stop();
                        this.apiError(err, 'تعذّر اعتماد الاقتراحات.');
                    });
            }).catch(() => { });
        },

        rejectSelected() {
            if (!this.pendingSelected.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'حدّد الاقتراحات المراد ردّها.');
                return;
            }

            // السبب مُلزَم — بدونه يعيد المرفق إرسال نفس الرقم بلا فائدة.
            this.$prompt('سبب الرد (يطّلع عليه المرفق):', 'ردّ الاقتراح', {
                confirmButtonText: 'ردّ',
                cancelButtonText: 'تراجع',
                inputValidator: (v) => (v && v.trim().length >= 3) || 'اكتب سبباً واضحاً',
            }).then(({ value }) => {
                const payload = {
                    Reason: value.trim(),
                    Lines: this.pendingSelected.map((r) => ({
                        BranchesId: r.branchesId,
                        NationalFormularyItemsId: r.nationalFormularyItemsId,
                    })),
                };
                this.$blockUI.Start();
                this.$http.RejectNeedProposals(payload)
                    .then((res) => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'تم', (res.data && res.data.message) || 'تم الرد.');
                        this.loadPending();
                    })
                    .catch((err) => {
                        this.$blockUI.Stop();
                        this.apiError(err, 'تعذّر ردّ الاقتراحات.');
                    });
            }).catch(() => { });
        },

        // ─── المقارنة حسب الصنف (وزارة) ──────────────────────────────────
        searchProducts(query) {
            this.productSearchLoading = true;
            this.$http.SearchNeedFormularyItems(query || null)
                .then((res) => {
                    this.productSearchLoading = false;
                    this.productOptions = (res && res.data && res.data.info) || [];
                })
                .catch(() => {
                    this.productSearchLoading = false;
                    this.productOptions = [];
                });
        },

        loadByProduct() {
            if (!this.selectedFormularyItemId) return;
            this.$blockUI.Start();
            this.$http.GetNeedEstimatesByProduct(this.selectedFormularyItemId)
                .then((res) => {
                    this.$blockUI.Stop();
                    const d = (res && res.data) || {};
                    this.productInfo = d.item || null;
                    this.productRows = d.info || [];
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.productInfo = null;
                    this.productRows = [];
                    this.apiError(err, 'تعذّر تحميل المقارنة.');
                });
        },

        switchMode(m) {
            this.mode = m;
            if (m === 'product' && !this.productOptions.length) this.searchProducts('');
            if (m === 'pending') this.loadPending();
        },
    },
};
